import React, { useEffect, useState } from 'react';
import type { SammelLinkPublic } from '../../../services/sifa/types';
import { ladeSammelLinkPublic } from '../../../services/sifa/sammellinkService';
import { describeError } from '../../../services/errorMessage';
import { datenschutzHref, teilnahmeHref } from './routenLogic';
import { kartenInfo } from './sammelLinkLogic';
import { TeilnahmeRahmen } from './TeilnahmeRahmen';
import { ALS_LINK, FEHLERKASTEN, HINWEISKASTEN, KNOPF_PRIMAER, TEXTLINK } from './teilnahmeSeiteStil';

type Stand =
  | { status: 'laedt' }
  | { status: 'bereit'; daten: SammelLinkPublic }
  | { status: 'ungueltig' }
  | { status: 'fehler'; meldung: string };

/**
 * Oeffentliche Seite des Sammel-Links #/unterweisungen/<token>
 * (docs/sifa-ausbau2-plan.md §0.5, §3.3): EIN QR am Aushang, dahinter alle
 * freigeschalteten Unterweisungen des Kunden. Was „freigeschaltet" heisst
 * (aktiv, Link an, nicht abgelaufen), entscheidet die RPC — die Seite zeigt
 * nur, was kommt. Kein Login, keine Anzeige „bereits erledigt" (§4).
 *
 * „Starten" fuehrt auf die normale Teilnehmerseite mit `?von=<token>`; die
 * zeigt dann den Ruecklink hierher.
 */
export const SammelLinkPage: React.FC<{ token: string }> = ({ token }) => {
  const [stand, setStand] = useState<Stand>({ status: 'laedt' });

  useEffect(() => {
    let aktiv = true;
    ladeSammelLinkPublic(token)
      .then((daten) => {
        if (aktiv) setStand(daten ? { status: 'bereit', daten } : { status: 'ungueltig' });
      })
      .catch((e) => {
        if (aktiv) setStand({ status: 'fehler', meldung: describeError(e) });
      });
    return () => {
      aktiv = false;
    };
  }, [token]);

  if (stand.status === 'laedt') {
    return (
      <TeilnahmeRahmen>
        <p className="text-slate-500" aria-live="polite">
          Unterweisungen werden geladen…
        </p>
      </TeilnahmeRahmen>
    );
  }

  if (stand.status === 'ungueltig') {
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">Dieser Link ist nicht mehr gültig</h1>
        <div className={HINWEISKASTEN}>
          Der Link zu den Unterweisungen ist abgeschaltet oder unbekannt. Bitte wende dich an die Person, von der du den
          Link bekommen hast.
        </div>
      </TeilnahmeRahmen>
    );
  }

  if (stand.status === 'fehler') {
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">Die Unterweisungen konnten nicht geladen werden</h1>
        <div className={FEHLERKASTEN}>{stand.meldung}</div>
        <p className="text-slate-600">Bitte prüfe die Internetverbindung und lade die Seite neu.</p>
      </TeilnahmeRahmen>
    );
  }

  const { kundeName, bueroName, unterweisungen } = stand.daten;

  return (
    <TeilnahmeRahmen>
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-slate-900">Unterweisungen für {kundeName || 'deinen Betrieb'}</h1>
        {bueroName && <p className="text-slate-600">{bueroName}</p>}
      </header>

      {unterweisungen.length === 0 ? (
        <div className={HINWEISKASTEN} role="status">
          Derzeit keine Unterweisung freigeschaltet. Bitte schau später noch einmal vorbei oder wende dich an deine
          Ansprechperson.
        </div>
      ) : (
        <>
          <p className="text-slate-600">
            Wähle die Unterweisung, die du machen sollst, und tippe auf „Starten". Am Ende bestätigst du mit deinen
            Angaben und deiner Unterschrift.
          </p>
          <ul className="space-y-3">
            {unterweisungen.map((u) => (
              <li key={u.linkToken} className="p-4 border border-slate-200 rounded-xl space-y-3">
                <div className="space-y-1">
                  <h2 className="text-lg font-bold text-slate-900 leading-snug">{u.titel}</h2>
                  {u.beschreibung && <p className="text-slate-600 line-clamp-3 whitespace-pre-line">{u.beschreibung}</p>}
                  <p className="text-sm text-slate-500">{kartenInfo(u)}</p>
                </div>
                <a
                  href={teilnahmeHref(u.linkToken, token)}
                  className={`${KNOPF_PRIMAER} ${ALS_LINK}`}
                  aria-label={`Starten: ${u.titel}`}
                >
                  Starten
                </a>
              </li>
            ))}
          </ul>
        </>
      )}

      <footer className="pt-3 border-t border-slate-100 text-base">
        <a href={datenschutzHref({ art: 'sammel', token })} target="_blank" rel="noopener" className={TEXTLINK}>
          Datenschutzhinweise
        </a>
      </footer>
    </TeilnahmeRahmen>
  );
};
